import { useIdentity, User } from "contexts/identity-context";
import { customDatasetsCreate } from "data/mutations";
import { useCallback, useState } from "react";
import { FileWithPath, useDropzone } from "react-dropzone";
import { useMutation, useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";

import * as QueryKeys from "data";
import {
  CreateCustomDatasetsMutationParams,
  CustomDatasetMutationRespose,
  DatasetError,
  UploadProgressMap,
  UseS3FileUploadDropperResult,
} from "./types";
import {
  completeMultipartUpload,
  getMultipartUploadData,
  multiPartUpload,
} from "./utils";

function createInitialUploadProgress() {
  const initialUploadProgress = {
    parts: [],
    percentComplete: -1,
    uploadDuration: -1,
    bytesPerSecond: -1,
    bytesRemaining: -1,
    estimatedSecondsRemaining: -1,
    averagingWindow: [],
    humanisedTimeRemaining: "??",
  };
  return initialUploadProgress;
}

type S3FileUploadDropperOptions = {
  maxFiles?: number;
  maxSize?: number;
  prefixFileName?: string;
  acceptExtension?: { [key: string]: string[] }; // { mime type: [extensions] }. https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types. Empty object allows all extensions.
};

export function useS3FileUploadDropper(
  options: S3FileUploadDropperOptions = {}
): UseS3FileUploadDropperResult {
  const {
    maxFiles = 0,
    maxSize = 51 * 1024 * 1024 * 1024,
    prefixFileName = "",
    acceptExtension = {},
  } = options;

  const awsRegion = "ap-southeast-2";

  const [uploadProgress, setUploadProgress] = useState<UploadProgressMap>({});
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [haveUploaded, setHaveUploaded] = useState<boolean>(false);
  const [multiPartFinalised, setMultiPartFinalised] = useState<string[]>([]);
  const [s3UploadFailed, setS3UploadFailed] = useState<boolean>(false);

  const [files, setFiles] = useState<FileWithPath[]>([]);

  type FileUploadAbortMap = { [key: string]: any };

  const [uploadAborts, setUploadAborts] = useState<FileUploadAbortMap>({});

  const [identity] = useIdentity();

  // Uploading has finished if the sum of the uploading progress is equal to the number of files being uploaded

  if (
    isUploading &&
    multiPartFinalised.length === files.length &&
    (Object.values(uploadProgress)
      .map((x) => x.percentComplete)
      .reduce((a: number, b: number) => a + b, 0) as number) /
      100 ===
      files.length
  ) {
    console.log("Upload completion check:", {
      multiPartFinalised: multiPartFinalised.length,
      filesLength: files.length,
      progressSum:
        Object.values(uploadProgress)
          .map((x) => x.percentComplete)
          .reduce((a: number, b: number) => a + b, 0) / 100,
    });
    if (files.length > 0) {
      console.log("✓ All uploads complete! Setting haveUploaded = true");
      setIsUploading(false);
      setHaveUploaded(true);
    } else {
      // Something went wrong and we are at 100% progress with 0 files
      console.log("⚠ Upload issue: 100% progress but 0 files");
      setIsUploading(false);
    }
  }

  const uploadFiles = async (bucket_name: string) => {
    console.log(
      "Starting upload for files:",
      files.length,
      files.map((f) => ({ name: f.name, path: f.path, size: f.size }))
    );
    console.log(
      "Already finalized:",
      multiPartFinalised.length,
      multiPartFinalised
    );

    // Reset upload state for new batch (but keep already uploaded files in multiPartFinalised)
    setIsUploading(true);
    setHaveUploaded(false);
    setUploadAborts({});

    // Initialize progress for all files at 0%
    setUploadProgress((prev) => {
      const newProgress = { ...prev };
      files.forEach((file: FileWithPath) => {
        const fileName = file.path || file.name;
        if (
          !newProgress[fileName] ||
          newProgress[fileName].percentComplete < 0
        ) {
          newProgress[fileName] = {
            ...createInitialUploadProgress(),
            percentComplete: 0, // Start at 0% instead of -1
          };
        }
      });
      return newProgress;
    });

    try {
      await Promise.all(
        files.map(async (acceptedFile: FileWithPath, index: number) => {
          console.log(
            `Processing file ${index}:`,
            acceptedFile.name,
            "path:",
            acceptedFile.path
          );

          if (!acceptedFile.path) {
            console.log(`SKIPPED file ${index}: No path property`);
            return;
          }

          // Skip files that have already been uploaded
          if (multiPartFinalised.includes(acceptedFile.path)) {
            console.log(`SKIPPED file ${index}: Already uploaded`);
            return;
          }

          // Skip files that are currently being uploaded (percentComplete between 0 and 100)
          const currentProgress = uploadProgress[acceptedFile.path];
          if (
            currentProgress &&
            currentProgress.percentComplete >= 0 &&
            currentProgress.percentComplete < 100
          ) {
            return;
          }

          const controller = new AbortController();

          let fileName = acceptedFile.name;
          if (prefixFileName) {
            fileName = prefixFileName + acceptedFile.name;
          }
          const multiPartUploadData = await getMultipartUploadData(
            fileName,
            bucket_name,
            acceptedFile.size,
            identity
          );
          const upload = multiPartUpload(
            multiPartUploadData,
            acceptedFile,
            setUploadProgress,
            controller
          );

          setUploadAborts((prevValue) => {
            return {
              ...prevValue,
              [acceptedFile.path as string]: () => {
                controller.abort("User cancelled");
              },
            };
          });

          try {
            console.log(`Uploading file ${index}: ${fileName}`);
            const uploadResult = await upload;
            console.log(`Completing multipart upload for ${fileName}`);
            await completeMultipartUpload(
              fileName,
              bucket_name,
              multiPartUploadData.uploadId,
              uploadResult,
              identity
            );
            console.log(`✓ Successfully uploaded: ${fileName}`);
            setMultiPartFinalised((prev) => {
              const updated = [...prev, acceptedFile.path as string];
              console.log(
                `MultiPartFinalised updated:`,
                updated.length,
                "/",
                files.length
              );
              return updated;
            });
          } catch (e) {
            console.error(`✗ Upload failed for ${fileName}:`, e);
            throw e;
            // Handle error here if needed
          }
        })
      );
      console.log("All uploads completed successfully");
    } catch (e) {
      console.error("Upload process failed:", e);
      setS3UploadFailed(true);
    }
  };

  const onDrop = useCallback((acceptedFiles: FileWithPath[]) => {
    console.log(
      "onDrop received files:",
      acceptedFiles.length,
      acceptedFiles.map((f) => f.path || f.name)
    );

    acceptedFiles = acceptedFiles.filter((x) => x.size > 0);
    console.log("After size filter:", acceptedFiles.length);

    const uniqueNewFiles = acceptedFiles
      .map((x) => (x.path ? x.path : ""))
      .filter(function (item, pos) {
        return acceptedFiles.map((x) => x.path).indexOf(item) == pos;
      });

    if (acceptedFiles.length !== uniqueNewFiles.length) {
      console.log("REJECTED: Duplicate files detected in batch", {
        totalFiles: acceptedFiles.length,
        uniqueFiles: uniqueNewFiles.length,
      });
      return;
    }

    let fileMap: UploadProgressMap = {};
    acceptedFiles.forEach((acceptedFile: FileWithPath) => {
      fileMap[acceptedFile.path as string] = createInitialUploadProgress();
    });
    setUploadProgress((oldProgress: UploadProgressMap) => {
      return {
        ...oldProgress,
        ...fileMap,
      };
    });

    setFiles((prevState: FileWithPath[]) => {
      const existingPaths = prevState.map((x) => x.path);

      return [
        ...prevState,
        ...acceptedFiles.filter((x) => !existingPaths.includes(x.path)),
      ];
    });
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: maxFiles,
    maxSize: maxSize,
    accept: acceptExtension,
  });

  const removeFile = (fileName: string) => {
    setFiles((prevState) => {
      const newState = prevState.filter((x) => x.path != fileName);
      if (!newState.length) {
        setHaveUploaded(() => false);
      }
      setUploadProgress((prevState) => {
        delete prevState[fileName];
        return prevState;
      });
      return newState;
    });
    if (isUploading && fileName in uploadAborts) {
      uploadAborts[fileName]();
      delete uploadAborts[fileName];
      if (Object.keys(uploadAborts).length === 0) {
        setIsUploading(false);
      }
    }
    setMultiPartFinalised((prev) => prev.filter((x) => x !== fileName));
  };
  return {
    uploadProgress,
    isUploading,
    haveUploaded,
    files,
    removeFile,
    getRootProps,
    getInputProps,
    uploadFiles,
    s3UploadFailed,
  };
}

export function useSubmitCustomDataset(
  apigeeDeveloperId: string,
  organisationId: string
) {
  const history = useHistory();
  const queryClient = useQueryClient();

  const [identityState] = useIdentity();
  const [customDatasetsError, setCustomDatasetsError] = useState<
    DatasetError[]
  >([]);
  const [submitted, setSubmitted] = useState<string[]>([]);
  const [successful, setSuccessful] = useState<string[]>([]);

  const customDatasetsCreateMutation = useMutation(
    (values: CreateCustomDatasetsMutationParams) => {
      const datasets = values.values.map((dataset) => {
        dataset.publish = values.publish;
        return dataset;
      });
      return customDatasetsCreate(
        datasets,
        apigeeDeveloperId,
        identityState as User
      );
    },
    {
      onSuccess: (
        response: CustomDatasetMutationRespose,
        values: CreateCustomDatasetsMutationParams
      ) => {
        setSuccessful([
          ...successful,
          ...values.values
            .filter(
              (x) => !response.errors.map((e) => e.id).includes(x.fileName)
            )
            .map((x) => x.fileName),
        ]);

        // Invalidate all custom datasets queries (matches all cache keys starting with this prefix)
        queryClient.invalidateQueries({ queryKey: [QueryKeys.adminCustomDatasetsSearchQuery] });

        setCustomDatasetsError(response.errors);
      },
    }
  );

  return {
    customDatasetsCreateMutation,
    customDatasetsError,
    customDatasetsSuccessful: successful,
  };
}
